import React, { useState, useEffect } from 'react';
import { AppAccessUpdateInput, APP_ACCESS_APP_NAMES } from '../../services/appAccessService';

interface EditAppAccessModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (id: string, data: AppAccessUpdateInput) => Promise<void>;
  appAccess: {
    id: string;
    organisation_id: string;
    organisation_name?: string;
    app_name: string;
    access_level: string;
    granted_by: string | null;
    granted_at: string;
    is_active: boolean;
    created_at: string;
    updated_at: string;
  } | null;
}

const EditAppAccessModal: React.FC<EditAppAccessModalProps> = ({
  isOpen,
  onClose,
  onSave,
  appAccess
}) => {
  const [formData, setFormData] = useState<AppAccessUpdateInput>({
    app_name: '',
    access_level: '',
    granted_by: null,
    granted_at: '',
    is_active: true
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);

  // Populate form data when appAccess changes
  useEffect(() => {
    if (appAccess) {
      setFormData({
        app_name: appAccess.app_name,
        access_level: appAccess.access_level,
        granted_by: appAccess.granted_by,
        granted_at: appAccess.granted_at,
        is_active: appAccess.is_active
      });
      setErrors({});
    }
  }, [appAccess]);

  const handleInputChange = (field: keyof AppAccessUpdateInput, value: string | boolean | null) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleGrantedAtChange = (value: string) => {
    const dateValue = value ? new Date(value).toISOString() : '';
    handleInputChange('granted_at', dateValue);
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!(formData.app_name ?? appAccess?.app_name)?.trim()) {
      newErrors.app_name = 'App name is required';
    }

    if (!formData.access_level?.trim()) {
      newErrors.access_level = 'Access level is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm() || !appAccess) {
      return;
    }

    setIsSaving(true);
    try {
      await onSave(appAccess.id, formData);
      onClose();
    } catch (error) {
      // Error handling is done in parent component
      console.error('Error updating app access:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    setErrors({});
    onClose();
  };

  if (!isOpen || !appAccess) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Edit App Access</h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 focus:outline-none"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Organisation - Read Only */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Organization
            </label>
            <input
              type="text"
              value={appAccess?.organisation_name || formData.organisation_id || ''}
              className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-500"
              placeholder="Organization"
              readOnly
              disabled
            />
          </div>

          {/* App Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              App Name *
            </label>
            <select
              value={formData.app_name ?? appAccess?.app_name ?? ''}
              onChange={(e) => handleInputChange('app_name', e.target.value)}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.app_name ? 'border-red-300' : 'border-gray-300'
              }`}
            >
              <option value="">Select app...</option>
              {APP_ACCESS_APP_NAMES.map(({ value, label }) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
            {errors.app_name && (
              <p className="text-red-500 text-xs mt-1">{errors.app_name}</p>
            )}
          </div>

          {/* Access Level */}
          {/* Access Level */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Access Level *
            </label>
            <select
              value={formData.access_level || ''}
              onChange={(e) => handleInputChange('access_level', e.target.value)}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.access_level ? 'border-red-300' : 'border-gray-300'
              }`}
            >
              <option value="">Select access level...</option>
              <option value="admin">Admin</option>
              <option value="edit">Edit</option>
              <option value="view">View</option>
            </select>
            {errors.access_level && (
              <p className="text-red-500 text-xs mt-1">{errors.access_level}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Granted At *
            </label>
            <input
              type="datetime-local"
              value={formData.granted_at ? new Date(formData.granted_at).toISOString().slice(0, 16) : ''}
              onChange={(e) => handleGrantedAtChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Granted By
            </label>
            <input
              type="text"
              value={formData.granted_by || ''}
              onChange={(e) => handleInputChange('granted_by', e.target.value || null)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter grantor (optional)"
            />
          </div>

          {/* Active Status */}
          <div className="flex items-center space-x-3">
            <label className="text-sm font-medium text-gray-700">Active</label>
            <button
              type="button"
              onClick={() => handleInputChange('is_active', !formData.is_active)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                formData.is_active ? 'bg-blue-600' : 'bg-gray-200'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  formData.is_active ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
            <span className="text-xs text-gray-500">
              {formData.is_active ? 'Active' : 'Inactive'}
            </span>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSaving}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 rounded-md transition-colors"
            >
              {isSaving ? 'Saving...' : 'Update Data'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditAppAccessModal;
